import {
    Calendar, Users, CheckCircle, AlertTriangle, ArrowRight, Camera, X, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Modal } from '@components/ui';
import { useState } from 'react';

/**
 * GunlukDetayModal — Saha raporu detay görüntüleme
 */
export default function GunlukDetayModal({ isOpen, onClose, rapor }) {
    if (!rapor) return null;
    const [lightboxIdx, setLightboxIdx] = useState(null);
    const photos = rapor.photoUrls || [];

    const openLightbox = (idx) => setLightboxIdx(idx);
    const closeLightbox = () => setLightboxIdx(null);
    const prevPhoto = (e) => { e.stopPropagation(); setLightboxIdx(i => (i - 1 + photos.length) % photos.length); };
    const nextPhoto = (e) => { e.stopPropagation(); setLightboxIdx(i => (i + 1) % photos.length); };

    const formatTarih = (tarihStr) => new Date(tarihStr).toLocaleDateString('tr-TR', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Rapor Detayı"
            size="lg"
            fullScreenMobile
        >
            <div className="space-y-5">
                {/* Header Info */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border-light">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Calendar size={24} className="text-primary" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-text-primary capitalize">
                                {formatTarih(rapor.tarih)}
                            </h2>
                            <p className="text-sm text-text-muted">Kaydeden: {rapor.kaydeden}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2 bg-bg-tertiary rounded-lg">
                        <Users size={16} className="text-text-muted" />
                        <span className="text-xl font-bold text-text-primary">{rapor.personelSayisi}</span>
                        <span className="text-xs text-text-muted">kişi</span>
                    </div>
                </div>

                {/* Günün Özeti */}
                <div>
                    <h3 className="text-sm font-semibold text-text-secondary mb-2">Günün Özeti</h3>
                    <p className="text-text-primary bg-bg-tertiary p-4 rounded-xl leading-relaxed text-sm">
                        {rapor.ozet}
                    </p>
                </div>

                {/* Yapılan İşler */}
                {rapor.yapilanIsler?.length > 0 && (
                    <div>
                        <h3 className="text-sm font-semibold text-text-secondary mb-2 flex items-center gap-2">
                            <CheckCircle size={15} className="text-success" />
                            Yapılan İşler ({rapor.yapilanIsler.length})
                        </h3>
                        <ul className="space-y-1.5">
                            {rapor.yapilanIsler.map((is, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-sm bg-success/5 border border-success/10 px-3 py-2 rounded-lg">
                                    <span className="text-success mt-0.5 shrink-0 text-xs">✓</span>
                                    <span className="text-text-primary">{is}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Sorunlar */}
                {rapor.sorunlar?.length > 0 && (
                    <div className="bg-warning/5 border border-warning/20 rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-warning mb-2 flex items-center gap-2">
                            <AlertTriangle size={15} />
                            Sorunlar / Aksaklıklar ({rapor.sorunlar.length})
                        </h3>
                        <ul className="space-y-1.5">
                            {rapor.sorunlar.map((sorun, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-sm">
                                    <span className="text-warning mt-0.5 shrink-0">⚠</span>
                                    <span className="text-text-primary">{sorun}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Yarın Planı */}
                {rapor.yarinPlani && (
                    <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-primary mb-2 flex items-center gap-2">
                            <ArrowRight size={15} />
                            Yarın Planı
                        </h3>
                        <p className="text-text-primary text-sm leading-relaxed">{rapor.yarinPlani}</p>
                    </div>
                )}

                {/* Saha Fotoğrafları */}
                {photos.length > 0 && (
                    <div>
                        <h3 className="text-sm font-semibold text-text-secondary mb-3 flex items-center gap-2">
                            <Camera size={15} className="text-text-muted" />
                            Saha Fotoğrafları ({photos.length})
                        </h3>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                            {photos.map((url, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => openLightbox(idx)}
                                    className="relative aspect-square rounded-xl overflow-hidden border border-border-light hover:border-primary/40 transition-all group cursor-pointer"
                                >
                                    <img src={url} alt={`Saha fotoğrafı ${idx + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Lightbox */}
                {lightboxIdx !== null && (
                    <div
                        className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center"
                        onClick={closeLightbox}
                    >
                        <button onClick={closeLightbox} className="absolute top-4 right-4 p-2 text-white hover:text-gray-300">
                            <X size={28} />
                        </button>
                        {photos.length > 1 && (
                            <>
                                <button onClick={prevPhoto} className="absolute left-4 p-2 text-white hover:text-gray-300">
                                    <ChevronLeft size={32} />
                                </button>
                                <button onClick={nextPhoto} className="absolute right-4 p-2 text-white hover:text-gray-300">
                                    <ChevronRight size={32} />
                                </button>
                            </>
                        )}
                        <img
                            src={photos[lightboxIdx]}
                            alt={`Fotoğraf ${lightboxIdx + 1}`}
                            className="max-w-[90vw] max-h-[90vh] object-contain rounded-xl"
                            onClick={e => e.stopPropagation()}
                        />
                        <p className="absolute bottom-4 text-white/60 text-sm">{lightboxIdx + 1} / {photos.length}</p>
                    </div>
                )}

                {/* Footer */}
                <div className="pt-3 border-t border-border-light text-xs text-text-muted flex items-center justify-between">
                    <span>Kaydeden: {rapor.kaydeden}</span>
                    <span>{new Date(rapor.createdAt).toLocaleString('tr-TR')}</span>
                </div>
            </div>
        </Modal>
    );
}
